import {
  Badge,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import { DfmSelectionsNone } from '@parallel-fluidics/shared/constants';
import { getDfmSelectionsString } from '@parallel-fluidics/shared/models/quotes';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePartAndQuote } from '../../features/firebase';

const formatUSD = (value) => Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const configLabel = (config) => (config === DfmSelectionsNone ? 'No optional selections' : config);

function PricingSummaryTable({ partId }) {
  const navigate = useNavigate();

  const { quote } = usePartAndQuote(partId);
  const { rows, correctVersion } = useMemo(() => {
    if (!quote) {
      return { correctVersion: false };
    }
    const { pricingV3 } = quote;
    if (!pricingV3) {
      return { correctVersion: false };
    }
    const { breakpoints, calculatedValues } = pricingV3?.values || {};
    if (!breakpoints || !calculatedValues) {
      return { correctVersion: true, rows: [] };
    }

    const {
      uncappedSetupPrice,
      cappedSetupPrice,
      uncappedUnscaledUnitPrice,
      cappedUnscaledUnitPrice,
      uncappedUnitPrice,
      cappedUnitPrice,
    } = calculatedValues;

    return {
      rows: breakpoints.map(({ quantity, multiplier }) => ({
        quantity,
        multiplier,
        uncappedSetupPrice,
        cappedSetupPrice,
        uncappedUnitPrice: uncappedUnitPrice * multiplier + (uncappedUnscaledUnitPrice || 0),
        cappedUnitPrice: cappedUnitPrice * multiplier + (cappedUnscaledUnitPrice || 0),
      })),
      correctVersion: true,
    };
  }, [quote]);

  // Which setup fee an order would use right now: the table above always shows the
  // full calculated setup, and a re-order of an already-ordered DFM configuration
  // gets the reduced reorderSetup instead — derived at pricing time, never written back
  const reorderInfo = useMemo(() => {
    const ordered = quote?.orderedDfmSelections;
    if (!Array.isArray(ordered) || ordered.length === 0) return null;
    const currentConfig = getDfmSelectionsString(quote.feedbackResponses);
    return {
      reorderSetup: quote.pricingV3?.values?.reorderSetup ?? 500,
      ordered,
      currentConfig,
      isReorder: ordered.includes(currentConfig),
    };
  }, [quote]);

  if (!correctVersion) {
    return (
      <Container
        header={(
          <Header variant="h2">
            Pricing
          </Header>
        )}
      >
        This quote uses an outdated pricing model. Ask
        {' '}
        <a href="https://slack.com/app_redirect?channel=UT9HGAKC4" target="_blank" rel="noreferrer">Andy</a>
        {' '}
        if you need help.
      </Container>
    );
  }

  return (
    <Table
      header={(
        <Header
          variant="h2"
          actions={rows.length > 0 ? (
            <Button variant="normal" onClick={() => { navigate(`/part/${partId}/pricing`); }}>Edit</Button>
          ) : null}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div>Pricing</div>
            {quote.isBudgetary && (
              <Badge color="severity-medium">Budgetary</Badge>
            )}
          </div>
        </Header>
      )}
      columnDefinitions={[
        { id: 'quantity', header: 'Quantity', cell: (row) => row.quantity },
        { id: 'multiplier', header: 'Multiplier', cell: (row) => row.multiplier },
        { id: 'uncappedSetupPrice', header: 'Uncapped Setup Price', cell: (row) => formatUSD(row.uncappedSetupPrice) },
        { id: 'uncappedUnitPrice', header: 'Uncapped Unit Price', cell: (row) => formatUSD(row.uncappedUnitPrice) },
        { id: 'cappedSetupPrice', header: 'Capped Setup Price', cell: (row) => formatUSD(row.cappedSetupPrice) },
        { id: 'cappedUnitPrice', header: 'Capped Unit Price', cell: (row) => formatUSD(row.cappedUnitPrice) },
      ]}
      items={rows}
      footer={reorderInfo ? (
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Re-order pricing</Box>
          <div>
            {`Re-order setup fee: ${formatUSD(reorderInfo.reorderSetup)} — applies when the current DFM selections match an ordered configuration`}
          </div>
          <div>
            {`Ordered configurations: ${reorderInfo.ordered.map(configLabel).join('; ')}`}
          </div>
          <div>
            {`Current selections: ${configLabel(reorderInfo.currentConfig)}`}
            {' — '}
            {reorderInfo.isReorder ? (
              <StatusIndicator type="success">
                {`re-order setup (${formatUSD(reorderInfo.reorderSetup)}) applies`}
              </StatusIndicator>
            ) : (
              <StatusIndicator type="info">
                full setup applies (configuration not ordered before)
              </StatusIndicator>
            )}
          </div>
        </SpaceBetween>
      ) : null}
      empty={(
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>Pricing needs review</b>
            <Button onClick={() => { navigate('./pricing'); }}>Review</Button>
          </SpaceBetween>
        </Box>
      )}
    />
  );
}

PricingSummaryTable.propTypes = {
  partId: PropTypes.string.isRequired,
};

export default PricingSummaryTable;
